import React, { useState, useEffect, useMemo } from 'react';
import { Form as BootstrapForm, FilterGrid, FilterField, type SearchFieldProps } from '@/components/ui';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { SearchableDropdown } from '../../../components/SearchableDropdown';
import FiltersCard from '../../../components/FiltersCard';
import type { WorkIdFilterOptions, WorkIdListFilters, WorkIdDateFilterType } from '../workIdService';
import { WORK_ID_STATUS_OPTIONS } from '../utils/workIdStatusConstants';
import { csvToValues, valuesToCsv } from '../utils/workIdQueryHelpers';
import { ASSET_TYPE_FILTER_OPTIONS } from '../../../constants/assetTypes';

type ValueLabelOption = { value: string; label: string };

const PRIORITY_OPTIONS: ValueLabelOption[] = [
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' }
];

const DATE_FILTER_TYPE_OPTIONS: { value: WorkIdDateFilterType; label: string }[] = [
  { value: 'reportedOn', label: 'Reported Date' },
  { value: 'taskCompletion', label: 'Completion Date (From Task)' }
];

type SourceDropdownRow = { key: string; label: string };

/** Filter card edits every list filter except free-text search (the header search field). */
export type WorkIdFiltersApplyPayload = Omit<WorkIdListFilters, 'search'>;

/** Multiselect filters: comma-separated string in state, array in the dropdown. */
type MultiFilterKey = 'serviceAreaId' | 'foremanAreaId' | 'assetType' | 'priority' | 'reportedById' | 'workSource';

interface WorkIdFiltersProps {
  filterOptions: WorkIdFilterOptions;
  filters: WorkIdListFilters;
  onClearFilters: () => void;
  onApplyFilters: (filters: WorkIdFiltersApplyPayload) => void;
  /**
   * True while a free-text search is applied. Clear All also clears the search, so it is enabled
   * for it; and the server lets a search reach the statuses the default view hides, so the
   * default Status no longer counts as hiding records while one is applied.
   */
  searchActive?: boolean;
  /** The page search, shown in the panel header bar (V2-077, SRCH-01). */
  search?: SearchFieldProps;
}

function normalizePending(f: WorkIdListFilters): WorkIdFiltersApplyPayload {
  return {
    serviceAreaId: f.serviceAreaId ?? '',
    foremanAreaId: f.foremanAreaId ?? '',
    assetType: f.assetType ?? '',
    status: f.status,
    priority: f.priority ?? '',
    dateFrom: f.dateFrom ?? '',
    dateTo: f.dateTo ?? '',
    dateFilterType: f.dateFilterType === 'taskCompletion' ? 'taskCompletion' : 'reportedOn',
    reportedById: f.reportedById ?? '',
    workSource: f.workSource ?? ''
  };
}

const WorkIdFilters: React.FC<WorkIdFiltersProps> = ({
  filterOptions,
  filters,
  onClearFilters,
  onApplyFilters,
  searchActive = false,
  search
}) => {
  const [pendingFilters, setPendingFilters] = useState<WorkIdFiltersApplyPayload>(() =>
    normalizePending(filters)
  );

  useEffect(() => {
    setPendingFilters(normalizePending(filters));
  }, [filters]);

  const filteredForemanAreas = useMemo(() => {
    const selectedServiceAreaIds = csvToValues(pendingFilters.serviceAreaId);
    const areas =
      selectedServiceAreaIds.length > 0
        ? filterOptions.foremanAreas.filter((area) =>
            selectedServiceAreaIds.includes(String(area.serviceAreaId))
          )
        : filterOptions.foremanAreas;
    return [...areas].sort((a, b) => a.name.localeCompare(b.name));
  }, [filterOptions.foremanAreas, pendingFilters.serviceAreaId]);

  const sourceOptions: SourceDropdownRow[] = useMemo(
    () =>
      filterOptions.sources.map((s) => ({
        key: s.kind === 'form' ? `form|${s.formId}` : `name|${encodeURIComponent(s.formName)}`,
        label: s.label
      })),
    [filterOptions.sources]
  );

  const handlePendingFilterChange = (name: keyof WorkIdFiltersApplyPayload, value: string) => {
    setPendingFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiFilterChange = (name: MultiFilterKey, values: (string | number)[]) => {
    const csv = valuesToCsv(values);
    setPendingFilters((prev) => {
      const next = { ...prev, [name]: csv };
      if (name === 'serviceAreaId' && csv !== prev.serviceAreaId) {
        // Keep only the foreman areas that still belong to a selected service area.
        const selectedServiceAreaIds = csvToValues(csv);
        if (selectedServiceAreaIds.length > 0) {
          const allowedForemanAreaIds = new Set(
            filterOptions.foremanAreas
              .filter((area) => selectedServiceAreaIds.includes(String(area.serviceAreaId)))
              .map((area) => String(area.id))
          );
          next.foremanAreaId = valuesToCsv(
            csvToValues(prev.foremanAreaId).filter((id) => allowedForemanAreaIds.has(id))
          );
        }
      }
      return next;
    });
  };

  const handleDateFilterTypeChange = (value: string) => {
    if (value === 'reportedOn' || value === 'taskCompletion') {
      setPendingFilters((prev) => ({ ...prev, dateFilterType: value }));
    }
  };

  const handleStatusChange = (values: (string | number)[]) => {
    const nextStatus = valuesToCsv(values) || 'exclude-cancelled';
    setPendingFilters((prev) => ({ ...prev, status: nextStatus }));
  };

  const applied = normalizePending(filters);

  const isDefaultStatus = applied.status === '' || applied.status === 'exclude-cancelled';

  /**
   * V2-076 / FLT-05: one flag per applied filter that hides records, the default "open statuses"
   * Status included (it hides Cancelled, Merged, Assigned, Closed and Closed Without Action) -
   * except while a search is applied, when the server searches those statuses too.
   */
  const appliedFilterCount = [
    applied.serviceAreaId !== '',
    applied.foremanAreaId !== '',
    applied.assetType !== '',
    !isDefaultStatus || !searchActive,
    applied.priority !== '' && applied.priority != null,
    (applied.dateFrom ?? '') !== '',
    (applied.dateTo ?? '') !== '',
    (applied.reportedById ?? '') !== '',
    (applied.workSource ?? '') !== '',
  ].filter(Boolean).length;

  const hasActiveFilters = () =>
    searchActive ||
    applied.serviceAreaId !== '' ||
    applied.foremanAreaId !== '' ||
    applied.assetType !== '' ||
    !isDefaultStatus ||
    (applied.priority !== '' && applied.priority != null) ||
    (applied.dateFrom ?? '') !== '' ||
    (applied.dateTo ?? '') !== '' ||
    (applied.reportedById ?? '') !== '' ||
    (applied.workSource ?? '') !== '';

  const hasUnappliedChanges = () =>
    pendingFilters.serviceAreaId !== applied.serviceAreaId ||
    pendingFilters.foremanAreaId !== applied.foremanAreaId ||
    pendingFilters.assetType !== applied.assetType ||
    pendingFilters.status !== applied.status ||
    pendingFilters.priority !== applied.priority ||
    (pendingFilters.dateFrom ?? '') !== (applied.dateFrom ?? '') ||
    (pendingFilters.dateTo ?? '') !== (applied.dateTo ?? '') ||
    (pendingFilters.dateFilterType ?? 'reportedOn') !== (applied.dateFilterType ?? 'reportedOn') ||
    (pendingFilters.reportedById ?? '') !== (applied.reportedById ?? '') ||
    (pendingFilters.workSource ?? '') !== (applied.workSource ?? '');

  const handleApplyFiltersClick = () => {
    onApplyFilters(pendingFilters);
  };

  const handleClearFilters = () => {
    const empty: WorkIdFiltersApplyPayload = {
      serviceAreaId: '',
      foremanAreaId: '',
      assetType: '',
      status: 'exclude-cancelled',
      priority: '',
      dateFrom: '',
      dateTo: '',
      dateFilterType: 'reportedOn',
      reportedById: '',
      workSource: ''
    };
    setPendingFilters(empty);
    onClearFilters();
  };

  return (
    <FiltersCard
      title="Work ID Filters"
      // Short enough to fit on one line on an iPad (W-iP11: the long help text was cut off with
      // "..."); the full explanation is in the panel body, where it wraps.
      subtitle="Default view hides closed, merged and cancelled Work IDs"
      collapsible={true}
      // FiltersCard starts collapsed on a phone and a tablet by itself (W-iP6).
      defaultCollapsed={false}
      appliedFilterCount={appliedFilterCount}
      search={search}
      showApplyButton={true}
      onApply={handleApplyFiltersClick}
      applyDisabled={!hasUnappliedChanges()}
      showClearButton={true}
      onClear={handleClearFilters}
      hasActiveFilters={hasActiveFilters()}
    >
      <BootstrapForm>
        <p className="tw-mb-3 tw-text-sm tw-text-content-muted" data-testid="work-id-filters-help">
          Filter by area, asset type, status, dates, priority, reporter and source. The default view excludes
          Cancelled, Merged, Assigned, Closed and Closed Without Action. Search can find those statuses too.
        </p>
        <FilterGrid>
          <FilterField>
            <MultiSelectDropdown<WorkIdFilterOptions['serviceAreas'][0]>
              options={filterOptions.serviceAreas}
              value={csvToValues(pendingFilters.serviceAreaId)}
              onChange={(values) => handleMultiFilterChange('serviceAreaId', values)}
              getValue={(area) => area.id.toString()}
              formatOption={(area) => area.name}
              placeholder="All Service Areas"
              className="tw-w-full"
              label="Service Area"
              noOptionsMessage="No service areas available"
            />
          </FilterField>

          <FilterField>
            <MultiSelectDropdown<WorkIdFilterOptions['foremanAreas'][0]>
              options={filteredForemanAreas}
              value={csvToValues(pendingFilters.foremanAreaId)}
              onChange={(values) => handleMultiFilterChange('foremanAreaId', values)}
              getValue={(area) => area.id.toString()}
              formatOption={(area) => area.name}
              placeholder="All Foreman Areas"
              className="tw-w-full"
              label="Foreman Area"
              noOptionsMessage="No foreman areas available for selected filters"
            />
          </FilterField>

          <FilterField>
            <MultiSelectDropdown<ValueLabelOption>
              options={ASSET_TYPE_FILTER_OPTIONS}
              value={csvToValues(pendingFilters.assetType)}
              onChange={(values) => handleMultiFilterChange('assetType', values)}
              getValue={(option) => option.value}
              formatOption={(option) => option.label}
              placeholder="All Asset Types"
              className="tw-w-full"
              label="Asset Type"
              noOptionsMessage="No asset types available"
              preserveOptionsOrder
              showSearch={false}
            />
          </FilterField>

          <FilterField>
            <MultiSelectDropdown<ValueLabelOption>
              options={WORK_ID_STATUS_OPTIONS}
              value={
                pendingFilters.status === 'exclude-cancelled' || !pendingFilters.status
                  ? []
                  : csvToValues(pendingFilters.status)
              }
              onChange={handleStatusChange}
              getValue={(option) => option.value}
              formatOption={(option) => option.label}
              placeholder="Open Statuses"
              className="tw-w-full"
              label="Status"
              noOptionsMessage="No status options available"
              preserveOptionsOrder
              showSearch={false}
            />
          </FilterField>

          {/* V2-025: no Form.Group around the date fields - its bottom margin lifted them ~12px
              above the dropdowns in the same row. Label + control, like every other field. */}
          <FilterField>
            <BootstrapForm.Label htmlFor="work-id-filter-date-from">Start Date</BootstrapForm.Label>
            <BootstrapForm.Control
              id="work-id-filter-date-from"
              type="date"
              value={pendingFilters.dateFrom ?? ''}
              onChange={(e) => handlePendingFilterChange('dateFrom', e.target.value)}
            />
          </FilterField>

          <FilterField>
            <BootstrapForm.Label htmlFor="work-id-filter-date-to">End Date</BootstrapForm.Label>
            <BootstrapForm.Control
              id="work-id-filter-date-to"
              type="date"
              value={pendingFilters.dateTo ?? ''}
              onChange={(e) => handlePendingFilterChange('dateTo', e.target.value)}
            />
          </FilterField>

          <FilterField>
            {/* The same dropdown control as the fields beside it (V2-025, FLT-02), single-select:
                one of the two date kinds always applies, so there is nothing to clear. */}
            <SearchableDropdown<{ value: WorkIdDateFilterType; label: string }>
              options={DATE_FILTER_TYPE_OPTIONS}
              value={pendingFilters.dateFilterType ?? 'reportedOn'}
              onChange={handleDateFilterTypeChange}
              getValue={(opt) => opt.value}
              formatOption={(opt) => opt.label}
              placeholder="Reported Date"
              className="tw-w-full"
              label="Filter Dates By"
              allowClear={false}
              preserveOptionsOrder
            />
          </FilterField>

          <FilterField>
            <MultiSelectDropdown<ValueLabelOption>
              options={PRIORITY_OPTIONS}
              value={csvToValues(pendingFilters.priority)}
              onChange={(values) => handleMultiFilterChange('priority', values)}
              getValue={(option) => option.value}
              formatOption={(option) => option.label}
              placeholder="All Priorities"
              className="tw-w-full"
              label="Priority"
              noOptionsMessage="No priorities available"
              preserveOptionsOrder
              showSearch={false}
            />
          </FilterField>

          <FilterField>
            <MultiSelectDropdown<WorkIdFilterOptions['reporters'][0]>
              options={filterOptions.reporters}
              value={csvToValues(pendingFilters.reportedById)}
              onChange={(values) => handleMultiFilterChange('reportedById', values)}
              getValue={(u) => u.id.toString()}
              formatOption={(u) =>
                [u.name, u.lastName].filter(Boolean).join(' ').trim() || `User #${u.id}`
              }
              placeholder="All Reporters"
              className="tw-w-full"
              label="Reported By"
              noOptionsMessage="No reporters found"
            />
          </FilterField>

          <FilterField>
            <MultiSelectDropdown<SourceDropdownRow>
              options={sourceOptions}
              value={csvToValues(pendingFilters.workSource)}
              onChange={(values) => handleMultiFilterChange('workSource', values)}
              getValue={(o) => o.key}
              formatOption={(o) => o.label}
              placeholder="All Sources"
              className="tw-w-full"
              label="Source"
              noOptionsMessage="No sources found"
            />
          </FilterField>
        </FilterGrid>
      </BootstrapForm>
    </FiltersCard>
  );
};

export default WorkIdFilters;
